<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { formatIdSource } from "@core/helpers"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            header: {
                footer: "Veuillez <strong>vérifier les données contractuelles et techniques</strong>, et <strong>indiquer la période de référence</strong> de la comparaison ci-dessous",
            },
            label: { SourceInfo: "Compteur" },
            status: {
                pending: "Workflow en attente",
                blocked: "Comparaison impossible",
                eligible: "Éligible",
            },
            errors: {
                UNSUPPORTED_CALENDRIER_FOURNISSEUR: {
                    title: "Impossible de comparer ce compteur",
                    content:
                        "Nos algorithmes ne traitent pas encore le type de calendrier fournisseur {type}",
                },
                CRASHED_WORKFLOW: {
                    title: "Erreur lors de la récupération des données",
                    content:
                        "Une erreur est survenue lors de la récupération des données du compteur. Veuillez réessayer plus tard.",
                },
            },
        },
    },
})
// ================================
// TYPES
// ================================
type MeterError =
    | "PERIODE_TOO_SHORT"
    | "UNSUPPORTED_CALENDRIER_FOURNISSEUR"
    | "CRASHED_WORKFLOW"
    | "PENDING_WORKFLOW"
    | null

// ================================
// STORE & REACTIVE STATE
// ================================
const comparatorStore = useComparatorStore()
const { sources, sourceSelected } = storeToRefs(comparatorStore)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Sources formatées avec toutes les informations nécessaires pour l'affichage
 * Inclut le statut, la validation et les erreurs métier
 */
const getSourcesFormated = computed(() => {
    return sources.value.map((source) => {
        const status = getWorkflowStatusBySourceId(source._id_source)
        const dateFrom = getDateFromSoutirageBySourceId(source._id_source)
        const errCode = computedErrorCode(source._id_source, {
            dateFrom,
            status,
        })

        let severity = "success"
        let icon = "pi pi-thumbs-up"
        let label = t("status.eligible")

        switch (errCode) {
            case "PERIODE_TOO_SHORT":
            case "UNSUPPORTED_CALENDRIER_FOURNISSEUR":
            case "CRASHED_WORKFLOW":
                severity = "danger"
                icon = "pi pi-times-circle"
                label = t("status.blocked")
                break
            case "PENDING_WORKFLOW":
                severity = "warn"
                icon = "pi pi-hourglass"
                label = t("status.pending")
                break
        }

        return {
            _id_source: source._id_source,
            idsource: formatIdSource(source.idsource, "pdl"),
            segment: getSegmentBySourceId(source._id_source),
            badge: {
                severity: severity,
                icon: icon,
                label: label,
            },
            calendrier_fournisseur: getCalendrierFournisseurBySourceId(
                source._id_source
            ),
            workflowStatus: status,
            dateFrom: getDateFromSoutirageBySourceId(source._id_source),
            error: errCode,
        }
    })
})

// ================================
// METHODS
// ================================

/**
 * Récupère une source par son identifiant unique
 * @param _id_source - Identifiant de la source
 * @returns La source correspondante ou undefined si non trouvée
 */
function findSourceById(_id_source: string) {
    return sources.value.find((source) => source._id_source === _id_source)
}

/**
 * Récupère l'état du workflow de récupération de données pour une source
 * @param _id_source - Identifiant de la source
 * @returns L'état du workflow ou null si non défini
 */
function getWorkflowStatusBySourceId(_id_source: string) {
    return (
        findSourceById(_id_source)?.informations?.pdl?.data_retrieval
            ?.historical?.state ?? null
    )
}

/**
 * Récupère le segment du compteur pour une source
 * @param _id_source - Identifiant de la source
 * @returns Le segment du compteur ou undefined
 */
function getSegmentBySourceId(_id_source: string) {
    return findSourceById(_id_source)?.informations?.pdl?.compteur?.segment
}

/**
 * Récupère la date de début des données de soutirage pour une source
 * @param _id_source - Identifiant de la source
 * @returns La date de début ou undefined
 */
const getDateFromSoutirageBySourceId = (_id_source: string) =>
    findSourceById(_id_source)?.informations?.pdl?.data?.soutirage?.cdc
        ?.date_from ?? undefined

/**
 * Récupère le code du calendrier fournisseur pour une source
 * @param _id_source - Identifiant de la source
 * @returns Le code calendrier fournisseur ou null si non défini
 */
function getCalendrierFournisseurBySourceId(_id_source: string) {
    return (
        findSourceById(_id_source)?.informations?.pdl?.structure_tarifaire
            ?.calendrier_fournisseur.code ?? null
    )
}

/**
 * Valide les contraintes métier sur la période et le type de calendrier
 * Vérifie l'historique des données et la compatibilité du calendrier
 * @param _id_source - Identifiant de la source à valider
 * @param dateFrom - Date de début des données
 * @param status - Statut du workflow
 * @returns Code d'erreur ou null si validation OK
 */
function computedErrorCode(
    _id_source: string,
    { dateFrom, status }: { dateFrom: Date | undefined; status: string | null }
): MeterError {
    // Gestion erreurs CRASHED_WORKFLOW
    if (status === "crashed") {
        return "CRASHED_WORKFLOW"
    }

    // Gestion erreurs PENDING_WORKFLOW
    if (status !== "finished") {
        return "PENDING_WORKFLOW"
    }

    if (!dateFrom) {
        return "PERIODE_TOO_SHORT"
    }

    // Gestion erreurs PERIODE_TOO_SHORT
    const end = new Date()
    if (dateFrom) {
        const limit = moment(end).subtract(1, "year").toDate()
        if (moment(dateFrom).isAfter(limit)) {
            return "PERIODE_TOO_SHORT"
        }
    }

    // Gestion erreurs UNSUPPORTED_CALENDRIER_FOURNISSEUR

    // // Validation du type de calendrier fournisseur
    // const calendrier_fournisseur = getCalendrierFournisseurBySourceId(_id_source)
    // if (!Object.values(CalendrierFournisseur).includes(calendrier_fournisseur as CalendrierFournisseur)) {
    //     return "UNSUPPORTED_CALENDRIER_FOURNISSEUR"
    // }

    return null
}

const controller = ref<AbortController | null>(null)

function handleSourceSelected() {
    controller.value?.abort()
    controller.value = new AbortController()
    // Lance la récupération de la meilleure période de consommation de référence
    comparatorStore.fetchPeriodAvailable(controller.value)

    // Lance la récupération des données
    comparatorStore.fetchDataPeriode(controller.value)

    // Récupère le code NAF de l'entreprise
    comparatorStore.fetchNafCodeBySIREN(controller.value)
}
</script>

<template>
    <div
        class="comparator-meter-choice-radio-list flex flex-col gap-6 items-start text-[var(--surface-800,#424242)]"
        role="radiogroup"
        :aria-label="t('label.SourceInfo')"
    >
        <!-- ================================ -->
        <!-- LISTE DES COMPTEURS -->
        <!-- ================================ -->
        <div
            v-for="source in getSourcesFormated"
            :key="source._id_source"
            class="flex flex-col gap-6 w-full"
        >
            <!-- Ligne principale du compteur -->
            <div class="flex flex-row items-center gap-3 transition-opacity">
                <!-- Sélection radio -->
                <BaseRadioButton
                    name="SourceInfo-choice"
                    :value="source._id_source"
                    v-model="sourceSelected"
                    :input-id="`source-${source._id_source}`"
                    @update:model-value="handleSourceSelected"
                />

                <!-- Label du compteur -->
                <label
                    :for="`source-${source._id_source}`"
                    class="cursor-pointer w-[9rem] text-sm"
                >
                    {{ formatIdSource(source.idsource, "pdl") }}
                </label>

                <!-- Type de compteur -->
                <BaseTag
                    :value="source.segment"
                    severity="primary"
                    icon="pi pi-bolt"
                    :pt="{
                        label: 'text-xs',
                    }"
                />

                <!-- Statut de validation -->
                <BaseTag
                    :value="source.badge.label"
                    :severity="source.badge.severity"
                    :icon="source.badge.icon"
                    :pt="{
                        label: 'text-xs',
                    }"
                />
            </div>

            <!-- ================================ -->
            <!-- MESSAGES D'ERREUR -->
            <!-- ================================ -->
            <Transition name="collapse">
                <ComparatorMeterHistoryChart
                    v-if="
                        sourceSelected === source._id_source &&
                        source.error === 'PERIODE_TOO_SHORT'
                    "
                />
                <ComparatorMeterWorkflowVisualiser
                    v-else-if="
                        sourceSelected === source._id_source &&
                        source.error === 'PENDING_WORKFLOW'
                    "
                />
                <div
                    v-else-if="
                        sourceSelected === source._id_source && source.error
                    "
                    class="w-full p-4 border border-gray-200 rounded-md"
                >
                    <BaseMessage
                        severity="error"
                        icon="pi pi-times-circle"
                        :closable="false"
                        class="w-full"
                    >
                        <strong>{{
                            t(`errors.${source.error}.title`, {
                                type: source.calendrier_fournisseur,
                            })
                        }}</strong>
                        <p class="mt-1">
                            {{
                                t(`errors.${source.error}.content`, {
                                    type: source.calendrier_fournisseur,
                                })
                            }}
                        </p>
                    </BaseMessage>
                </div>
            </Transition>
        </div>
    </div>
</template>

<style scoped>
/* ================================ */
/* COMPOSANT PRINCIPAL */
/* ================================ */

/* ================================ */
/* BADGES DE STATUT */
/* ================================ */
.p-tag.p-tag-success {
    background-color: #f4fcf7 !important;
    color: #188a42 !important;
}
.p-tag.p-tag-warn {
    background-color: #fff8f3 !important;
    color: #ae510f !important;
}
.p-tag.p-tag-danger {
    background-color: #fff5f5 !important;
    color: #b32b23 !important;
}

/* ================================ */
/* ANIMATIONS */
/* ================================ */
.collapse-enter-from,
.collapse-leave-to {
    max-height: 0;
    opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
    max-height: 240px;
    opacity: 1;
}
.collapse-enter-active,
.collapse-leave-active {
    transition:
        max-height 220ms ease,
        opacity 180ms ease;
    overflow: hidden;
}
</style>
